import { Injectable } from '@angular/core';
import { U2bColumnDefinition, U2bColumnDefinitionBrakePoint } from '@core/components/layout/table/table.types';
import { BcmUserTableSettingKey } from '@shared/models/bcm-settings-user';

export type TableDisplayedColumnProperties = Record<U2bColumnDefinitionBrakePoint, readonly string[]>;

/**
 * Holds the currently rendered table columns per tableId so export uses the same
 * visibility and order as the on-screen list (not only persisted defaults).
 */
@Injectable({ providedIn: 'root' })
export class TableExportColumnRegistry {

    private readonly _columnsByTableId = new Map<BcmUserTableSettingKey | string, U2bColumnDefinition[]>();

    /** Data-column properties the header renders per breakpoint (without checkbox/actions). */
    private readonly _displayedPropertiesByTableId = new Map<BcmUserTableSettingKey | string, TableDisplayedColumnProperties>();

    private readonly _breakpointByTableId = new Map<BcmUserTableSettingKey | string, U2bColumnDefinitionBrakePoint>();

    setColumnDefinitions(tableId: BcmUserTableSettingKey | string, columnDefinitions: U2bColumnDefinition[]): void {
        this._columnsByTableId.set(tableId, columnDefinitions);
    }

    setDisplayedColumnProperties(tableId: BcmUserTableSettingKey | string, displayed: TableDisplayedColumnProperties): void {
        // Copies: TableComponent appends 'actions'/'checkbox' to the same arrays right after.
        this._displayedPropertiesByTableId.set(tableId, {
            [U2bColumnDefinitionBrakePoint.Desktop]: [...displayed[U2bColumnDefinitionBrakePoint.Desktop]],
            [U2bColumnDefinitionBrakePoint.Tablet]: [...displayed[U2bColumnDefinitionBrakePoint.Tablet]],
            [U2bColumnDefinitionBrakePoint.Mobile]: [...displayed[U2bColumnDefinitionBrakePoint.Mobile]],
        });
    }

    setBreakpoint(tableId: BcmUserTableSettingKey | string, breakpoint: U2bColumnDefinitionBrakePoint): void {
        this._breakpointByTableId.set(tableId, breakpoint);
    }

    getColumnDefinitions(tableId: BcmUserTableSettingKey | string): U2bColumnDefinition[] | undefined {
        return this._columnsByTableId.get(tableId);
    }

    getDisplayedColumnProperties(
        tableId: BcmUserTableSettingKey | string,
        breakpoint: U2bColumnDefinitionBrakePoint,
    ): readonly string[] | undefined {
        return this._displayedPropertiesByTableId.get(tableId)?.[breakpoint];
    }

    /** Breakpoint the table currently renders, so "Aktuelle Ansicht" exports what is on screen. */
    getBreakpoint(tableId: BcmUserTableSettingKey | string): U2bColumnDefinitionBrakePoint {
        return this._breakpointByTableId.get(tableId) ?? U2bColumnDefinitionBrakePoint.Desktop;
    }

    clearColumnDefinitions(tableId: BcmUserTableSettingKey | string): void {
        this._columnsByTableId.delete(tableId);
        this._displayedPropertiesByTableId.delete(tableId);
        this._breakpointByTableId.delete(tableId);
    }
}
